/**
 * BATCH 6C Part 3 — Pending Reimbursements report.
 * Lists every vendor expense paid via a personal card ("Personal Card —
 * Reimbursable"), grouped by person, so accounts can settle up. Marking a
 * row "Reimbursed" only flips a flag on the expense — it never auto-creates
 * a ledger entry (that stays a manual/expense-form action elsewhere).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wallet, CheckCircle2, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

export default function VendorReimbursements() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/accounting/reimbursements/pending`, { withCredentials: true });
      setGroups(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load pending reimbursements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markReimbursed = async (expenseId) => {
    setUpdating(expenseId);
    try {
      await axios.put(`${BACKEND_URL}/api/accounting/expenses/${expenseId}/reimbursement`, { reimbursementStatus: 'reimbursed' }, { withCredentials: true });
      toast.success('Marked as reimbursed');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally {
      setUpdating('');
    }
  };

  const grandTotal = groups.reduce((s, g) => s + (g.total || 0), 0);

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="vendor-reimbursements-page">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Wallet className="h-6 w-6 text-amber-600" /> Pending Reimbursements
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Vendor expenses paid out of a personal card ("Personal Card — Reimbursable"), grouped by person, awaiting settlement.
        </p>
      </header>

      <Card className="p-4 flex items-center justify-between border border-amber-200 bg-amber-50/60" data-testid="reimbursements-grand-total-card">
        <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">Total pending across all people</span>
        <span className="text-xl font-bold font-mono text-amber-800" data-testid="reimbursements-grand-total">{inr(grandTotal)}</span>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
      ) : groups.length === 0 ? (
        <Card className="p-12 text-center" data-testid="reimbursements-empty">
          <CheckCircle2 className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
          <p className="text-slate-500">No pending reimbursements — all settled up.</p>
        </Card>
      ) : (
        groups.map((g) => (
          <ReimbursementGroupCard key={g.person} g={g} updating={updating} onMarkReimbursed={markReimbursed} />
        ))
      )}
    </div>
  );
}

const ReimbursementGroupCard = ({ g, updating, onMarkReimbursed }) => {
  const entries = g.entries || [];
  return (
    <Card className="overflow-hidden" data-testid={`reimbursement-group-${g.person}`}>
      <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-800">{g.person}</span>
        <span className="text-sm font-mono font-bold text-amber-700">{inr(g.total)}</span>
      </div>
      <div className="divide-y divide-slate-50">
        {entries.map((e) => (
          <ReimbursementRow key={e.expenseId} e={e} updating={updating} onMarkReimbursed={onMarkReimbursed} />
        ))}
      </div>
    </Card>
  );
};

const ReimbursementRow = ({ e, updating, onMarkReimbursed }) => (
  <div className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`reimbursement-row-${e.expenseId}`}>
    <div>
      <p className="text-sm font-medium text-slate-900">{e.description}</p>
      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-500">
        <span>{e.vendorName || '—'}</span>
        <span>·</span>
        <span>{e.paymentDate || 'No date'}</span>
        <Badge variant="outline" className="text-[10px] font-mono">•••• {e.cardLast4}</Badge>
      </div>
    </div>
    <div className="flex items-center gap-3 shrink-0">
      <span className="text-sm font-mono font-semibold text-slate-900">{inr(e.paidAmount)}</span>
      <Button
        size="sm"
        variant="outline"
        disabled={updating === e.expenseId}
        onClick={() => onMarkReimbursed(e.expenseId)}
        data-testid={`mark-reimbursed-btn-${e.expenseId}`}
        className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
      >
        <Clock className="h-3.5 w-3.5 mr-1" /> {updating === e.expenseId ? 'Saving…' : 'Mark Reimbursed'}
      </Button>
    </div>
  </div>
);
